import React from 'react';
import ReactDOM from 'react-dom/client';
import axios from 'axios';
import App from './App';
import './styles/global.css';
import { getInitialApiOrigin, getNextApiOrigin, persistApiOrigin } from './services/apiHost';

axios.defaults.baseURL = getInitialApiOrigin();

async function bootstrapRedirectSession() {
  const redirectParams = new URLSearchParams(window.location.search);
  const transferCode = redirectParams.get('code');
  const forcedApiOrigin = redirectParams.get('api_origin');

  if (forcedApiOrigin) {
    try {
      const normalizedOrigin = new URL(forcedApiOrigin).origin;
      // Aceita apenas origens no mesmo hostname que a janela atual (previne redirect externo forjado).
      if (new URL(normalizedOrigin).hostname === window.location.hostname) {
        axios.defaults.baseURL = normalizedOrigin;
        persistApiOrigin(normalizedOrigin);
      }
    } catch (_) {
      // Ignora origin inválida e mantém fallback padrão.
    }
  }

  if (!transferCode) {
    return;
  }

  try {
    const response = await axios.post('/api/auth/exchange-transfer-code', { code: transferCode });
    const { token, usuario, login } = response.data;

    if (token && usuario) {
      localStorage.setItem('erp_token', token);
      localStorage.setItem('erp_usuario', usuario);
      localStorage.setItem('erp_login', login || usuario);
    }
  } catch (_) {
    localStorage.removeItem('erp_token');
    localStorage.removeItem('erp_usuario');
    localStorage.removeItem('erp_login');
  } finally {
    window.history.replaceState({}, document.title, window.location.pathname);
  }
}

// Restaura token salvo ao recarregar a página
const savedToken = localStorage.getItem('erp_token');
if (savedToken) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
}

// Interceptor: se token expirar (401), desloga automaticamente
axios.interceptors.response.use(
  (response) => {
    if (response?.config?.baseURL) {
      persistApiOrigin(response.config.baseURL);
    }
    return response;
  },
  (error) => {
    const config = error?.config;
    const method = (config?.method || 'get').toLowerCase();
    const isIdempotentMethod = ['get', 'head', 'options'].includes(method);

    // Retry em porta alternativa quando houver erro de rede
    if (!error?.response && config && isIdempotentMethod) {
      const currentOrigin = config.baseURL || axios.defaults.baseURL;
      const attempted = config._attemptedApiOrigins || [currentOrigin];
      const nextOrigin = getNextApiOrigin(currentOrigin, attempted);

      if (nextOrigin) {
        config._attemptedApiOrigins = [...attempted, nextOrigin];
        config.baseURL = nextOrigin;
        axios.defaults.baseURL = nextOrigin;
        persistApiOrigin(nextOrigin);
        return axios.request(config);
      }
    }

    if (error.response?.status === 401) {
      localStorage.removeItem('erp_token');
      localStorage.removeItem('erp_usuario');
      delete axios.defaults.headers.common['Authorization'];
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

const root = ReactDOM.createRoot(document.getElementById('root'));

bootstrapRedirectSession().finally(() => {
  const persistedToken = localStorage.getItem('erp_token');
  if (persistedToken) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${persistedToken}`;
  }

  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
});
